import React from 'react';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { canUserChangeStatus, getBunkeringStatusEnumDisplayName } from '../../BunkeringUtils';
import { BunkeringStatusEnum } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { BunkeringStatusChangeOptionsWrapper, BunkeringStatusChangeWrapper, BunkeringStatusButton } from '../../Bunkering.styles';
import { useAuthStore } from 'src/context/AuthContext';
import { isDateBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { getInvalidFieldsText } from './BunkerRegStatusUtils';
import { WarningText } from 'src/components/shared/warning-text/WarningText.style';
import { BunkerRegistrationStatusIcon } from './BunkerRegistrationStatusIcon';
import Tooltip from 'src/components/ui/tooltip/Tooltip';

interface BunkerRegStatusChangeProps {
    bunkering: Bunkering;
    invalidFields: string[];
    validLiterWeight: boolean;
    featureEnabledDate?: Date | null;
    initiateStatusChange: (status: BunkeringStatusEnum) => void;
}

const BunkerRegStatusChange: React.FC<BunkerRegStatusChangeProps> = ({
    bunkering,
    invalidFields,
    validLiterWeight,
    featureEnabledDate,
    initiateStatusChange,
}) => {
    const { role } = useAuthStore();

    const renderStatusOptions = () => {
        switch (bunkering?.currentStatus?.status) {
            case BunkeringStatusEnum.Registered:
                return (
                    <>
                        <BunkeringStatusButton onClick={() => initiateStatusChange(BunkeringStatusEnum.Approved)} disabled={invalidFields.length > 0}>
                            Approve
                        </BunkeringStatusButton>

                        <BunkeringStatusButton onClick={() => initiateStatusChange(BunkeringStatusEnum.InUse)} disabled={!validLiterWeight}>
                            Bypass approval
                        </BunkeringStatusButton>
                        <BunkeringStatusButton onClick={() => initiateStatusChange(BunkeringStatusEnum.Rejected)}>Reject</BunkeringStatusButton>
                        {featureEnabledDate && isDateBefore(bunkering.bunkeringDateTime, featureEnabledDate) && (
                            <Tooltip
                                type="information"
                                text='If this fuel has already been consumed before being registered, please select "Spent" and disregard the approval process.'
                            >
                                <BunkeringStatusButton onClick={() => initiateStatusChange(BunkeringStatusEnum.Spent)}>Spent</BunkeringStatusButton>
                            </Tooltip>
                        )}
                    </>
                );
            case BunkeringStatusEnum.Approved:
                return (
                    <>
                        <BunkeringStatusButton onClick={() => initiateStatusChange(BunkeringStatusEnum.Rejected)}>Reject</BunkeringStatusButton>
                    </>
                );
            case BunkeringStatusEnum.Rejected:
                return (
                    <>
                        <BunkeringStatusButton onClick={() => initiateStatusChange(BunkeringStatusEnum.Debunkered)}>Debunkered</BunkeringStatusButton>
                    </>
                );
            case BunkeringStatusEnum.InUse:
                return (
                    <>
                        {!bunkering?.bunkeringStatuses?.some((status) => status.status === BunkeringStatusEnum.Approved) && (
                            <BunkeringStatusButton
                                onClick={() => initiateStatusChange(BunkeringStatusEnum.Approved)}
                                disabled={invalidFields.length > 0}
                            >
                                Approve
                            </BunkeringStatusButton>
                        )}
                        <BunkeringStatusButton onClick={() => initiateStatusChange(BunkeringStatusEnum.Debunkered)}>Debunkered</BunkeringStatusButton>
                        <BunkeringStatusButton onClick={() => initiateStatusChange(BunkeringStatusEnum.Rejected)}>Reject</BunkeringStatusButton>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <BunkeringStatusChangeWrapper>
            {role.isWriter && bunkering?.currentStatus?.status && bunkering?.currentStatus?.status !== BunkeringStatusEnum.Debunkered && (
                <div>
                    {invalidFields.length > 0 && (
                        <WarningText style={{ marginTop: '0' }}>
                            The following fields in the bunker registration form are missing or invalid: {getInvalidFieldsText(invalidFields)}. Please
                            update the data to allow approval.
                        </WarningText>
                    )}
                    <BunkeringStatusChangeOptionsWrapper>
                        Current status:{' '}
                        {getBunkeringStatusEnumDisplayName(
                            bunkering?.currentStatus?.status,
                            bunkering?.currentStatus?.activeFromDate,
                            bunkering?.approvedForUseAt
                        )}{' '}
                        {BunkerRegistrationStatusIcon(bunkering?.currentStatus?.status, bunkering.bunkeringStatuses ?? [])}
                        {canUserChangeStatus(bunkering?.currentStatus) && (
                            <>
                                {' | Change status:'}
                                {renderStatusOptions()}
                            </>
                        )}
                    </BunkeringStatusChangeOptionsWrapper>
                </div>
            )}
        </BunkeringStatusChangeWrapper>
    );
};

export default BunkerRegStatusChange;
